/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-props-no-spreading */
import * as React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
} from "@mui/material";
import { TransitionProps } from "notistack";
import {
  updateOne as updateClient,
  validate as validateClient,
  type Client,
} from "./data/clients";
import { useNotification } from "../../context/notification-provider";
import ClientForm, { ClientFormState, FormFieldValue } from "./client-form";
import { ClaimType, ClientInformation, ClientType } from "./data/types";
import LoadingScreen from "../progress/loading-screen";
import useClaimTypes from "./data/use-claim-types";
import useClientTypes from "./data/use-client-types";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ClientUpdate = ({
  selectedClient,
  open,
  onClose,
}: {
  selectedClient: ClientInformation;
  open: boolean;
  onClose: () => void;
}) => {
  const {
    data: clientTypes,
    error: loadingError,
    isLoading,
  } = useClientTypes();

  const {
    data: claimTypes,
    error: loadingCTError,
    isLoading: isCTLoading,
  } = useClaimTypes();

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { showNotification } = useNotification();

  const [formState, setFormState] = React.useState<ClientFormState>({
    values: {
      id: selectedClient?.id,
      type: selectedClient?.type,
      claimType: selectedClient?.claimType,
      name: selectedClient?.name,
      enabled: selectedClient?.enabled ?? false,
    },
    errors: {},
  });
  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = React.useCallback(
    (newFormValues: Partial<ClientFormState["values"]>) => {
      setFormState((previousState: ClientFormState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = React.useCallback(
    (newFormErrors: Partial<ClientFormState["errors"]>) => {
      setFormState((previousState: ClientFormState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = React.useCallback(
    (name: keyof ClientFormState["values"], value: FormFieldValue) => {
      const validateField = async (
        values: Partial<ClientFormState["values"]>,
      ) => {
        const { issues } = validateClient(values);
        setFormErrors({
          ...formErrors,
          [name]: issues?.find((issue: any) => issue.path?.[0] === name)
            ?.message,
        });
      };

      let newFormValues = { ...formValues };

      let updatedValue: ClientType | ClaimType | FormFieldValue | undefined =
        value;

      if (name === "type") {
        const selectedClientType = clientTypes?.find(
          (type) => type.id === value,
        );
        updatedValue = selectedClientType;
      }
      if (name === "claimType") {
        const selectedClaimType = claimTypes?.find((type) => type.id === value);
        updatedValue = selectedClaimType;

        if (selectedClaimType) newFormValues.claimType = selectedClaimType;
      }

      newFormValues = { ...newFormValues, [name]: updatedValue };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [
      formValues,
      setFormValues,
      setFormErrors,
      formErrors,
      clientTypes,
      claimTypes,
    ],
  );

  const handleFormSubmit = React.useCallback(async () => {
    const { issues } = validateClient(formValues);

    if (issues && issues.length > 0) {
      setFormErrors(
        Object.fromEntries(
          issues.map((issue: { path?: (keyof Client)[]; message?: string }) => [
            issue.path?.[0] as keyof ClientFormState["values"],
            issue.message,
          ]),
        ),
      );
      return;
    }
    setFormErrors({});
    setIsSubmitting(true);
    try {
      await updateClient(formValues as Partial<ClientInformation>);

      showNotification({
        message: "Client updated successfully.",
        severity: "success",
        duration: 3000,
      });
      setIsSubmitting(false);
      onClose();
    } catch (updateError) {
      showNotification({
        message: `Failed to update client. Reason: ${(updateError as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formValues, setFormErrors, showNotification, onClose]);

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      maxWidth="md"
      fullWidth
      slots={{
        transition: Transition,
      }}
    >
      <DialogTitle sx={{ borderBottom: 2, mb: 2, borderColor: "divider" }}>
        Edit Client
      </DialogTitle>
      {isLoading || isCTLoading ? (
        <LoadingScreen />
      ) : (
        <DialogContent sx={{ pb: 0 }}>
          {loadingError || loadingCTError ? (
            <Alert severity="error">
              Unable to load claim or client types.
            </Alert>
          ) : (
            <ClientForm
              formState={formState}
              clientTypes={clientTypes || []}
              claimTypes={claimTypes || []}
              onFieldChange={handleFormFieldChange}
              onSubmit={handleFormSubmit}
            />
          )}
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleFormSubmit}
          loading={isSubmitting}
        >
          Update client
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default ClientUpdate;
